import { Box, Card, CardHeader, CardMedia, TablePagination } from '@mui/material';
import ImageOutlinedIcon from '@mui/icons-material/ImageOutlined';
import { IStory } from '@opengov/stories-shared/types';
import moment from 'moment';
import React, { useEffect, useRef, useState } from 'react';
import { IStoryListMessages } from '../../../../types';
import { purgeUrlCache } from '../../../utils';
import { styles } from './StoryCardGrid.styles';

type PaginationOptions = {
  totalRows: number;
  rowsPerPage: number;
  setRowsPerPage: (rowsPerPage: number) => void;
  currentPage: number;
  setPage: (page: number) => void;
  itemName: string;
};

type Props = {
  showGlobalNavBar: boolean;
  stories: IStory[];
  isTemplate: boolean;
  messages: IStoryListMessages;
  storiesUrl: string;
  paginationOptions?: PaginationOptions;
  onClick(story: IStory, isTemplate: boolean): void;
};

export function StoryCardGrid({ stories, isTemplate, messages, storiesUrl, paginationOptions, onClick }: Props) {
  const containerRef = useRef<HTMLDivElement>(null);
  const [maxHeight, setMaxHeight] = useState<string>('calc(100vh - 410px)');

  useEffect(() => {
    const updateHeight = () => {
      if (containerRef.current) {
        const offsetTop = containerRef.current.getBoundingClientRect().top;
        const paginationHeight = paginationOptions ? 52 : 0;
        setMaxHeight(`calc(100vh - ${offsetTop + paginationHeight + 8}px)`);
      }
    };
    updateHeight();
    const observer = new ResizeObserver(updateHeight);
    if (containerRef.current) observer.observe(document.body);
    window.addEventListener('resize', updateHeight);
    return () => {
      observer.disconnect();
      window.removeEventListener('resize', updateHeight);
    };
  }, [paginationOptions]);

  return (
    <Box sx={styles.wrapper}>
      <Box ref={containerRef} sx={{ ...styles.container, maxHeight }}>
        <Box sx={styles.cardGrid}>
          {stories.map((story) => {
            let imageSrc: string | undefined;
            if (story.thumbnail_relative_path != undefined) {
              imageSrc = `${storiesUrl}/${story.thumbnail_relative_path}`;
            } else {
              imageSrc = story.thumbnail;
            }

            const formattedAuthor = `${story.author.name.first} ${story.author.name.last}`;
            const metadata = `${messages.createdBy} ${formattedAuthor} ${moment.utc(story.createdAt).fromNow()}`;

            return (
              <Card
              key={story._id}
              sx={styles.card}
              onClick={() => onClick(story, isTemplate)}
              role="button"
              tabIndex={0}
              onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') onClick(story, isTemplate); }}
            >
                {imageSrc ? (
                  <CardMedia component="img" image={purgeUrlCache(imageSrc)} alt={story.title} sx={styles.cardMedia} />
                ) : (
                  <Box sx={styles.cardMediaPlaceholder} aria-hidden="true">
                    <ImageOutlinedIcon sx={styles.cardMediaPlaceholderIcon} />
                  </Box>
                )}
                <CardHeader title={story.title} subheader={metadata} sx={styles.cardHeader} />
              </Card>
            );
          })}
        </Box>
      </Box>
      {paginationOptions && (
        <TablePagination
          component="div"
          count={paginationOptions.totalRows}
          page={paginationOptions.currentPage - 1}
          rowsPerPage={paginationOptions.rowsPerPage}
          rowsPerPageOptions={[20, 40, 60]}
          onPageChange={(_, newPage) => paginationOptions.setPage(newPage + 1)}
          onRowsPerPageChange={(e) => paginationOptions.setRowsPerPage(parseInt(e.target.value, 10))}
          sx={styles.paginationWrapper}
        />
      )}
    </Box>
  );
}
